"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useLanguage } from "@/reusable_sections/Header";
const translations = {
  en: {
    badge: "06 / Consultation",
    heading: "Secure Your Consultation",
    subhead: "Expert guidance across immigration pathways, tax advisory, real estate, family mediation, and international legal matters from Stegy Group's know-how hub of specialists.",
    disclaimer: "Professional services. Confidential and secure.",
    scheduleAnother: "Schedule Another Session"
  },
  fr: {
    badge: "06 / Consultation",
    heading: "Sécurisez votre consultation",
    subhead: "Conseils d'experts en matière d'immigration, fiscalité, immobilier, médiation familiale et questions juridiques internationales au sein du pôle de spécialistes de Stegy Group.",
    disclaimer: "Services professionnels. Confidentiels et sécurisés.",
    scheduleAnother: "Planifier une autre session"
  },
  ht: {
    badge: "06 / Konsiltasyon",
    heading: "Sekirize konsiltasyon ou",
    subhead: "Konsèy ekspè sou pwosesis imigrasyon, konsiltasyon fiskal, byen imobilye, mediyasyon familyal ak kesyon legal entènasyonal pa ekip espesyalis Stegy Group.",
    disclaimer: "Sèvis pwofesyonèl. Konfidansyèl ak sekirize.",
    scheduleAnother: "Planifye yon lòt sesyon"
  }
};
function InnerCircleForm() {
  const {
    lang: rawLang
  } = useLanguage();
  const lang = rawLang === "fr" || rawLang === "ht" ? rawLang : "en";
  const t = translations[lang];
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mx-auto max-w-xl border border-border bg-background/40 p-8 text-center">
        <p className="font-default text-2xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 font-mono text-xs uppercase tracking-widest">
          {t.scheduleAnother}
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="mx-auto w-full max-w-3xl space-y-10">
      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="JOHN DOE" className="h-12 border-x-0 border-t-0 border-b border-border bg-transparent px-0 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px] uppercase tracking-widest" />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="EMAIL@EXAMPLE.COM" className="h-12 border-x-0 border-t-0 border-b border-border bg-transparent px-0 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px] uppercase tracking-widest" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone_number" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Phone Number — Optional
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className="h-12 border-x-0 border-t-0 border-b border-border bg-transparent px-0 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px] uppercase tracking-widest" />
          </FormItem>} />

      {submitError && <div className="border border-destructive/50 bg-destructive/10 p-4">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive-foreground">
            {submitError}
          </p>
        </div>}

      <div className="flex flex-col items-center gap-6 pt-4 sm:flex-row sm:justify-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="h-12 bg-primary px-8 font-mono text-xs uppercase tracking-[0.25em] text-primary-foreground hover:bg-primary/90">
          {isSubmitting ? "Processing…" : "Book Consultation"}
          <ArrowRight className="ml-2 size-4" />
        </Button>
      </div>
    </Form>;
}
export default function SecureYourConsultation() {
  const {
    lang: rawLang
  } = useLanguage();
  const lang = rawLang === "fr" || rawLang === "ht" ? rawLang : "en";
  const t = translations[lang];
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: subheadRef,
    isVisible: subheadVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="795" id="secure-your-consultation" className="dark bg-background text-foreground relative overflow-hidden py-32 md:py-48 px-6">
      <div aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 hidden -translate-y-1/2 lg:block opacity-[0.15]">
        <div className="h-80 w-48 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_consultation_left.webp)]" />
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-0 top-1/2 hidden -translate-y-1/2 lg:block opacity-[0.15]">
        <div className="h-80 w-48 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_consultation_right.png)]" />
      </div>

      <div className="relative mx-auto max-w-4xl text-center">
        <div ref={labelRef} className={`font-mono text-[11px] uppercase tracking-[0.3em] text-primary transition-all duration-700 ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span>{t.badge}</span>
        </div>

        <h2 ref={headingRef} className="font-default font-serif mt-10 leading-[1.05] text-foreground !text-4xl" style={{
        letterSpacing: "-0.03em",
        opacity: headingVisible ? 1 : 0,
        transform: headingVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 800ms ease 100ms, transform 800ms ease 100ms"
      }}>
          {t.heading}
        </h2>

        <p ref={subheadRef} className={`font-default mx-auto mt-8 max-w-2xl text-lg italic text-muted-foreground md:text-xl transition-all duration-700 delay-200 ${subheadVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {t.subhead}
        </p>

        <div className="mx-auto mt-12 h-px w-16 bg-border" />

        <div ref={formRef} className={`mt-16 transition-all duration-700 delay-300 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <FormProvider formKey="consultation_form_home" formId="consultation_form_home" sectionName="secure-your-consultation" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email_address: {
            required: true,
            pattern: "email"
          }
        }} submitText="Book Consultation" successMessage="Your consultation request has been received. We will contact you shortly to finalize the details." errorMessage="An error occurred. Please verify your information and try again.">
            <InnerCircleForm />
          </FormProvider>

          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            {t.disclaimer}
          </p>
        </div>
      </div>
    </section>;
}